// Environment variable validation using Zod

import { z } from 'zod';

const envSchema = z.object({
  // Authentication Configuration
  VITE_AUTH_BASE_URL: z.string().url('VITE_AUTH_BASE_URL must be a valid URL'),
  VITE_AUTH_CLIENT_ID: z.string().min(1, 'VITE_AUTH_CLIENT_ID is required'),
  VITE_AUTH_REDIRECT_URL: z.string().url('VITE_AUTH_REDIRECT_URL must be a valid URL'),

  // API Configuration
  VITE_API_BASE_URL: z
    .string()
    .url('VITE_API_BASE_URL must be a valid URL'),

  // Application Configuration
  VITE_APP_NAME: z
    .string()
    .default('AskiiMall Marketplace'),
  VITE_APP_VERSION: z
    .string()
    .default('1.0.0'),
  VITE_APP_ENVIRONMENT: z
    .enum(['development', 'staging', 'production'])
    .default('development'),
});

// Validate environment variables
const validateEnv = () => {
  try {
    return envSchema.parse(import.meta.env);
  } catch (error) {
    if (error instanceof z.ZodError) {
      const missingVars = error.errors.map(err => err.path.join('.')).join(', ');
      throw new Error(
        `Missing or invalid environment variables: ${missingVars}\n` +
        'Please check your .env.local file and ensure all required variables are set.'
      );
    }
    throw error;
  }
};

// Export validated environment variables
export const env = validateEnv();

// Type for environment variables
export type Env = z.infer<typeof envSchema>;

// Helper function to check if we're in development
export const isDevelopment = () => env.VITE_APP_ENVIRONMENT === 'development';

// Helper function to check if we're in production
export const isProduction = () => env.VITE_APP_ENVIRONMENT === 'production';

// Helper function to check if we're in staging
export const isStaging = () => env.VITE_APP_ENVIRONMENT === 'staging';
